<script setup lang="ts">
import {computed} from 'vue';
import {useSessionWeight} from '@/ts/composables/stateful/use-session-weight';
import {useUnitConversion} from '@/ts/composables/pure/use-unit-conversion';
import {useProfileDateFormat} from '@/ts/composables/stateful/use-profile-date-format';
import {useUserStore} from '@/ts/store/user-store';

const props = defineProps<{
  selectedPercentage: number | null;
  /** Optional weight in grams to use instead of store value (for read-only mode) */
  currentWeight?: number | null;
}>();

const {latestWeight} = useSessionWeight();
const {userData} = useUserStore();

const {formatWeight} = useUnitConversion({
  countryCode: userData?.countryCode,
  locale: useProfileDateFormat().value.numberLocale,
});

const currentWeightGrams = computed(() => props.currentWeight ?? latestWeight.value);

const weightLossPercent = computed(() => {
  if (!props.selectedPercentage) {
    return null;
  }
  // If value is >= 1, assume it's already a decimal (e.g., 0.05 = 5%)
  // Otherwise assume it's a percentage (shouldn't happen in this case)
  return props.selectedPercentage < 1 ? props.selectedPercentage : props.selectedPercentage / 100;
});

const weightLossGrams = computed(() => {
  if (!currentWeightGrams.value || !weightLossPercent.value) {
    return null;
  }
  return currentWeightGrams.value * weightLossPercent.value;
});

const targetWeightGrams = computed(() => {
  if (!currentWeightGrams.value || !weightLossGrams.value) {
    return null;
  }
  return currentWeightGrams.value - weightLossGrams.value;
});

const isReady = computed(() => {
  return currentWeightGrams.value !== null && props.selectedPercentage !== null;
});

const percentageDisplay = computed(() => {
  if (!weightLossPercent.value) {
    return '';
  }
  return (weightLossPercent.value * 100).toFixed(0);
});

// Format weight values - all stored weights are in grams
const currentWeightFormatted = computed(() => {
  if (!currentWeightGrams.value) {return '';}
  return formatWeight(currentWeightGrams.value);
});

const weightLossFormatted = computed(() => {
  if (!weightLossGrams.value) {return '';}
  return formatWeight(weightLossGrams.value);
});

const targetWeightFormatted = computed(() => {
  if (!targetWeightGrams.value) {return '';}
  return formatWeight(targetWeightGrams.value);
});

</script>
<template>
  <div v-if="isReady" class="weight-loss-calculation">
    <div class="calculation-title text-heading-3">
      {{ $t('weight_loss_goal_title') }}
    </div>
    <div class="calculation-content">
      <p class="text-body">
        {{ $t('weight_loss_goal_description', {
          weight: currentWeightFormatted,
          percentage: percentageDisplay,
        }) }}
      </p>
      <ul class="calculation-list">
        <li>
          {{ $t('weight_loss_goal_lose') }}:
          <strong>{{ weightLossFormatted }}</strong>
        </li>
        <li>
          {{ $t('weight_loss_goal_target') }}:
          <strong>{{ targetWeightFormatted }}</strong>
        </li>
      </ul>
    </div>
  </div>
</template>
<style scoped lang="scss">
.weight-loss-calculation {
  padding: var(--Spacing-spacing-4);
  background-color: var(--Neutral-UI-neutral-ui-5);
  border-radius: var(--Radius-radius-2);
}

.calculation-title {
  margin-bottom: var(--Spacing-spacing-3);
  color: var(--Text-Primary-text-primary);
}

.calculation-content {
  color: var(--Text-Primary-text-primary);
}

.calculation-list {
  margin-top: var(--Spacing-spacing-1);
  padding-left: var(--Spacing-spacing-5);

  li {
    margin-bottom: var(--Spacing-spacing-2);
  }
}
</style>
